import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { startCheckout } from "@/threedvista/TdvPricing";
import { useProduct } from "@/threedvista/product";

export function TdvShell({ title, sub, children }) {
  const P = useProduct();
  const g = P.key === "giraffe";
  return (
    <div className="min-h-screen bg-[#0B1220] text-white grid lg:grid-cols-2" data-testid="tdv-auth-shell">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-[radial-gradient(circle_at_20%_20%,#1e3a5f_0,transparent_55%)]">
        <Link to={P.base} className="font-serif text-2xl" data-testid="tdv-auth-logo">Showhome <span className="text-[#C9A227]">for {P.name}</span></Link>
        <p className="font-serif text-5xl leading-tight max-w-md">Your {P.name} tour, with your brand and a sales team built in.</p>
        <p className="text-sm text-white/50">{g ? "Paste your Giraffe360 tour link. We do the rest." : "Upload the folder 3DVista publishes. We do the rest."}</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to={P.base} className="lg:hidden font-serif text-xl block mb-10">Showhome <span className="text-[#C9A227]">for {P.name}</span></Link>
          <h1 className="font-serif text-4xl">{title}</h1>
          {sub && <p className="text-sm text-white/60 mt-2 mb-8">{sub}</p>}
          {children}
        </div>
      </div>
    </div>
  );
}

const inCls = "h-12 bg-white/5 border-white/15 text-white placeholder:text-white/30";
const btn = "w-full h-12 rounded-full bg-[#C9A227] text-slate-950 font-semibold disabled:opacity-50 transition-opacity hover:opacity-90";

function useForm(init) {
  const [f, setF] = useState(init);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const run = (fn) => async (e) => { e.preventDefault(); setErr(""); setBusy(true); try { await fn(); } catch (x) { setErr(errMsg(x)); } setBusy(false); };
  const bind = (k, extra = {}) => ({ value: f[k], onChange: (e) => setF({ ...f, [k]: e.target.value }), className: inCls, ...extra });
  return { f, err, busy, run, bind };
}

const Err = ({ err }) => (err ? <p className="text-sm text-rose-300" data-testid="tdv-auth-error">{err}</p> : null);

export function TdvSignup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const want = params.get("plan") ? { plan: params.get("plan"), ai: params.get("ai") || "ours", cycle: params.get("cycle") || "month" } : null;
  const { f, err, busy, run, bind } = useForm({ business_name: "", name: "", email: "", password: "" });
  const P = useProduct();
  const submit = run(async () => { const { data } = await api.post(`${P.auth}/signup`, f); setUser(data); if (!(want && await startCheckout(want))) nav(`${P.base}/app`); });
  return (
    <TdvShell title="Create your account" sub={want ? `Step 1 of 2: create your account. Next you'll pay for the ${want.plan[0].toUpperCase()}${want.plan.slice(1)} plan.` : "Create your account, then choose a plan to add tours."}>
      <form onSubmit={submit} className="space-y-4" data-testid="tdv-signup-form">
        <Input required placeholder="Business name" maxLength={120} {...bind("business_name", { "data-testid": "tdv-signup-business" })} />
        <Input required placeholder="Your name" maxLength={120} {...bind("name", { "data-testid": "tdv-signup-name" })} />
        <Input required type="email" placeholder="Email" {...bind("email", { "data-testid": "tdv-signup-email" })} />
        <Input required type="password" minLength={8} placeholder="Password (8+ characters)" {...bind("password", { "data-testid": "tdv-signup-password" })} />
        <Err err={err} />
        <button disabled={busy} className={btn} data-testid="tdv-signup-submit">{busy ? "Creating…" : want ? "Create account and pay" : "Create account"}</button>
        <p className="text-sm text-white/60 text-center">Have an account? <Link to={`${P.base}/login${want ? `?plan=${want.plan}&ai=${want.ai}&cycle=${want.cycle}` : ""}`} className="text-white underline" data-testid="tdv-goto-login">Log in</Link></p>
      </form>
    </TdvShell>
  );
}

export function TdvLogin() {
  const P = useProduct();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const { f, err, busy, run, bind } = useForm({ email: "", password: "" });
  const [lp] = useSearchParams();
  const want = lp.get("plan") ? { plan: lp.get("plan"), ai: lp.get("ai") || "ours", cycle: lp.get("cycle") || "month" } : null;
  const submit = run(async () => { const { data } = await api.post(`${P.auth}/login`, { email: f.email.trim(), password: f.password }); setUser(data); const next = new URLSearchParams(window.location.search).get("next"); if (next === `${P.base}/try`) { nav(next); return; } if (!(want && await startCheckout(want))) nav(`${P.base}/app`); });
  return (
    <TdvShell title="Log in" sub={`Your ${P.name} tours, leads and branding.`}>
      <form onSubmit={submit} className="space-y-4" data-testid="tdv-login-form">
        <Input required type="email" placeholder="Email" {...bind("email", { "data-testid": "tdv-login-email" })} />
        <Input required type="password" placeholder="Password" {...bind("password", { "data-testid": "tdv-login-password" })} />
        <Err err={err} />
        <button disabled={busy} className={btn} data-testid="tdv-login-submit">{busy ? "Signing in…" : "Log in"}</button>
        <div className="flex justify-between text-sm text-white/60">
          <Link to={`${P.base}/forgot`} className="hover:text-white" data-testid="tdv-goto-forgot">Forgot password?</Link>
          <Link to={`${P.base}/signup`} className="hover:text-white" data-testid="tdv-goto-signup">Create an account</Link>
        </div>
      </form>
    </TdvShell>
  );
}

export function TdvForgot() {
  const [sent, setSent] = useState(false);
  const { f, err, busy, run, bind } = useForm({ email: "" });
  const P = useProduct();
  const submit = run(async () => { await api.post(`${P.auth}/forgot`, { email: f.email.trim() }); setSent(true); });
  return (
    <TdvShell title="Reset your password" sub="We'll email you a link to choose a new one.">
      {sent ? <p className="text-white/80" data-testid="tdv-forgot-sent">If that email has an account, a reset link is on its way. Check your spam folder too.</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="tdv-forgot-form">
          <Input required type="email" placeholder="Email" {...bind("email", { "data-testid": "tdv-forgot-email" })} />
          <Err err={err} />
          <button disabled={busy} className={btn} data-testid="tdv-forgot-submit">Send reset link</button>
        </form>
      )}
      <p className="text-sm text-white/60 mt-6"><Link to={`${P.base}/login`} className="underline">Back to log in</Link></p>
    </TdvShell>
  );
}

export function TdvReset() {
  const P = useProduct();
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const { f, err, busy, run, bind } = useForm({ password: "" });
  const submit = run(async () => { await api.post("/tdv/auth/reset", { token: params.get("token") || "", password: f.password }); setDone(true); });
  return (
    <TdvShell title="Choose a new password">
      {done ? <p className="text-white/80" data-testid="tdv-reset-done">Password changed. <Link to={`${P.base}/login`} className="underline">Log in</Link></p> : (
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="tdv-reset-form">
          <Input required type="password" minLength={8} placeholder="New password (8+ characters)" {...bind("password", { "data-testid": "tdv-reset-password" })} />
          <Err err={err} />
          <button disabled={busy} className={btn} data-testid="tdv-reset-submit">Save password</button>
        </form>
      )}
    </TdvShell>
  );
}
